import React from 'react';
import { WppTypography } from '@wppopen/components-library-react';
import styles from './ContentCases.module.scss';
import { Localization, SelectionHighlighted, SideModalTitle } from 'utils';

const { Main, MainFooter, MainFooterContent, MainStrong, MainList } = styles;

export const ContentCases: any = {
    [Localization.US]: {
        [SideModalTitle.POPULATION_GROUPS]: (
            <div className={Main}>
                <div>
                    <WppTypography type="s-strong">Summary</WppTypography>
                    <WppTypography type="s-body">
                        This tab showcases population trends for US adults (18+). The default
                        setting is the entire adult (18+) US population. In the remainder of the
                        product, all aggregations, projections, and alternative future scenarios are
                        based on this adult (18+) population.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Context for our definitions</WppTypography>
                    <WppTypography type="s-body">
                        Based on our discussions with GroupM multicultural experts, please note the
                        following:
                    </WppTypography>
                    <ul className={MainList}>
                        <li>
                            The “Hispanic, any race” category covers all Hispanic backgrounds,
                            regardless of race.
                        </li>
                        <li>
                            Other ethnically diverse groups exclude Hispanics. For example, “Black”
                            includes only non-Hispanic Black individuals, avoiding double counting.
                        </li>
                        <li>
                            The Segment Opportunity Simulator refers to “ethnically diverse groups“
                            as “ethnic groups“, encompassing American Indian & Alaska Natives(AIAN)
                            , Native Hawaiian & Pacific Islanders (NHPI), and others, but excluding
                            “Mixed & Other Hispanic“, which falls under “Hispanic, any race“.
                        </li>
                    </ul>
                </div>
                <div>
                    <WppTypography type="s-strong">Adult population</WppTypography>
                    <br />
                    <WppTypography type="s-body">
                        <ul className={MainList}>
                            <li>The adult population is defined as 18+.</li>
                        </ul>
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Diverse Populations</WppTypography>
                    <br />
                    <WppTypography type="s-body">
                        <ul className={MainList}>
                            <li>
                                <span className={MainStrong}>Black: </span>Individuals identified as
                                “Black“ in the survey, excluding “Black, Hispanic“, who are included
                                under “Hispanic, any race“.
                            </li>
                            <li>
                                <span className={MainStrong}>Asian: </span> Includes ethnicities
                                such as Indian, Japanese, Chinese, etc., but excludes “Asian,
                                Hispanic“, who are included under “Hispanic, any race“.
                            </li>
                            <li>
                                <span className={MainStrong}>Mixed race + Additional races: </span>
                                Individuals of multiple racial backgrounds or those from smaller,
                                less represented races.
                            </li>
                            <li>
                                <span className={MainStrong}>Hispanic, any race: </span> An ethnic
                                group that can be of any race but identifies with a Hispanic ethnic
                                background, as defined by the US Census Bureau. Includes Chicano,
                                Chilean, Mexican, etc.
                            </li>
                            <li>
                                <span className={MainStrong}>White: </span>Individuals who
                                identified as “White“ as their only race. Those who identified with
                                other races and/or ethnicities are not included in this category.
                            </li>
                        </ul>
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Interpreting plot tooltips</WppTypography>
                    <WppTypography type="s-body">
                        <ul className={MainList}>
                            <li>
                                When you Group by ”Total population”, the ”Adult population (18+)”
                                value represents the total adults and” Total population” represents
                                the total population, inclusive of children.
                            </li>
                            <li>
                                Other “Group By” options provide different tooltips reflecting the
                                grouping and aggregation for that dropdown. For example, “Combined
                                Multicultural” shows “White” and “Ethnically Diverse Groups” to
                                highlight the growth in all Ethnic Groups combined vs, the steady
                                decline of the White population.
                            </li>
                        </ul>
                    </WppTypography>
                </div>
                <div className={MainFooter}>
                    <div className={MainFooterContent}>
                        <WppTypography type="s-strong">Sample insights</WppTypography>
                        <WppTypography type="s-body">
                            <ul className={MainList}>
                                <li>
                                    <span className={MainStrong}>Audience definition: </span>All
                                    geographical regions, All ethnicities, All ages, All sexes.
                                </li>
                                <li>
                                    <span className={MainStrong}>Group by: </span>Combined
                                    Multicultural.
                                </li>
                                <li>
                                    <span className={MainStrong}>Show by: </span>Geography.
                                </li>
                            </ul>
                            <br />
                            The plots indicate that the adult population (18+) of ethnically diverse
                            groups is growing, and the White segment is projected to slightly
                            decline across all geographical regions by 2040. In terms of regional
                            trends, ethnically diverse groups will outgrow the White population by
                            2039 in the South, and 2027 in the West.
                        </WppTypography>
                    </div>
                </div>
            </div>
        ),
        [SideModalTitle.DEMOGRAPHIC_GROUPS]: (
            <div className={Main}>
                <div>
                    <WppTypography type="s-strong">Summary</WppTypography>
                    <WppTypography type="s-body">
                        This tab provides demographic breakdowns of the population projections.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Note on definitions</WppTypography>
                    <WppTypography type="s-body">
                        See the “Learn more” modal in the Population tab for details about Ethnic
                        group definitions. Sex is mutually exclusive. This is strictly based on the
                        taxonomy provided in the source datasets.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Interpreting plot tooltips</WppTypography>
                    <br />
                    <WppTypography type="s-body">
                        The ”Adult Population” value to the left of the parentheses represents the
                        population projection you`re hovering over in the plot. The corresponding
                        number in parentheses (”n%”) represents the projection as a percentage out
                        of the total values in the plot. The ”Total up to this point” value
                        represents the total population of the group and all the groups below it in
                        the legend (stacked from bottom to top).
                    </WppTypography>
                </div>
                <div className={MainFooter}>
                    <div className={MainFooterContent}>
                        <WppTypography type="s-strong">Sample insights</WppTypography>
                        <WppTypography type="s-body">
                            <ul className={MainList}>
                                <li>
                                    <span className={MainStrong}>Audience definition: </span>All
                                    geographical regions, All ethnicities, All ages, All sexes.
                                </li>
                                <li>
                                    <span className={MainStrong}>Group by: </span>Ethnic group.
                                </li>
                                <li>
                                    <span className={MainStrong}>Show by: </span>Age.
                                </li>
                            </ul>
                            <br />
                            Over 50% of younger consumers (18-49) will be ethnically/racially
                            diverse (non-White) by 2040. The takeaway is that an increasing number
                            of new consumers from diverse groups will come of age and enter the
                            market, expanding your business opportunities.
                            <br />
                            <br />
                            Learn about possible individual disposable income trajectories for
                            different ethnic groups by specifying disposable income in the dropdowns
                            on the left-hand side. For more details, see the Business Opportunity
                            tab.
                        </WppTypography>
                    </div>
                </div>
            </div>
        ),
        [SideModalTitle.MOBILITY_SCENARIOS]: (
            <div className={Main}>
                <div>
                    <WppTypography type="s-strong">Summary</WppTypography>
                    <WppTypography type="s-body">
                        This tab showcases how US disposable income may evolve over time based on
                        different socioeconomic mobility levels. The projections build on the
                        insights in the previous tabs by introducing four alternative future
                        scenarios based on socioeconomic mobility evolutions. The recommended
                        socioeconomic mobility is the ”Baseline” scenario and the remainder of the
                        product bases the projections on this scenario.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Mobility scenarios</WppTypography>
                    <WppTypography type="s-body">
                        There are four potential socioeconomic mobility scenarios. In every
                        scenario, the population grows over time. As a result, total individual
                        disposable income for the population also increases. The recommended
                        socioeconomic mobility is the ”Baseline” scenario and the remainder of the
                        product bases the projections on the ”Baseline”. Different scenarios reflect
                        the overarching trend of growing diverse purchase power, albeit the exact
                        quantitative values changing.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">No socioeconomic mobility</WppTypography>
                    <WppTypography type="s-body">
                        Assumes that future individual disposable income will be constant based on
                        2023 levels.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Baseline</WppTypography>
                    <WppTypography type="s-body">
                        Assumes that socioeconomic mobility trends for geo-demographic group from
                        the past several years will continue into the future before slowing down and
                        saturating at 2030.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Rapid socioeconomic mobility</WppTypography>
                    <WppTypography type="s-body">
                        A variant of the Baseline scenario where upward mobility increases more
                        rapidly. In the context of upward mobility, this scenario implies a rapid
                        improvement in socioeconomic status. This involves increased access to
                        education, better job opportunities, and higher individual disposable
                        income.
                        <br />
                        <br />
                        This is represented by a delayed saturation point that occurs at 2040.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Restricted socioeconomic mobility</WppTypography>
                    <WppTypography type="s-body">
                        A variant of the Baseline scenario with delayed socioeconomic mobility.
                        Restricted socioeconomic mobility refers to a situation where individuals or
                        groups face significant barriers or limitations in their ability to move up
                        or down the social and economic ladder. This restriction can be due to
                        various factors such as limited access to education, discriminatory
                        practices, economic inequality, and lack of opportunities.
                        <br />
                        <br />
                        This is represented by an accelerated saturation point that occurs at 2025.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Interpreting plot tooltips</WppTypography>
                    <WppTypography type="s-body">
                        If you select “Socioeconomic mobility” under “Group by”, the tooltip value
                        will represent individual disposable income per scenario.
                        <br />
                        If you select any other option under “Group by”, then each number to the
                        left of the parentheses represents the individual disposable income value
                        you’re hovering over in the plot. The corresponding number in parentheses
                        (“n%”) represents the projection as a percentage out of the total individual
                        disposable income in the plot.
                    </WppTypography>
                </div>
                <div className={MainFooter}>
                    <div className={MainFooterContent}>
                        <WppTypography type="s-strong">Sample insights</WppTypography>
                        <WppTypography type="s-body">
                            Audience definition: All geo regions, All ethnicities, all ages, all
                            sexes, all incomes
                            <br />
                            Group by: Ethnic group.
                            <br />
                            Show by: Socioeconomic scenario
                            <br />
                            <br />
                            Everyone wins when underserved populations are financially empowered.
                            ethnically/racially diverse (non-White) groups gain more than $12.17
                            trillion in cumulative individual disposable income [1] in a world where
                            they have rapid socioeconomic mobility vs no mobility.
                            <br />
                            <br />
                            Disposable income is projected to stabilize for the White group and grow
                            substantially for ethnically/ratio diverse groups in most scenarios.
                            These insights are particularly visible in the Per Year view, which
                            allows you to do a year-by-year comparison.
                            <br />
                            <br />
                            Explore category spend for different ethnic groups and income levels in
                            the Current Spend tab.
                            <br />
                            <br />
                            [1] Total individual income for diverse groups under no socioeconomic
                            mobility is 66.55T: $4.55T (Mixed race + Additional races) + $24.76T
                            (Hispanic, any race) + $17.19T (Black) + $15.13T (Asian).
                            <br />
                            Total individual income for diverse groups under rapid socioeconomic
                            mobility is 78.72T: $6.07T (Mixed race + Additional races) + $31.41T
                            (Hispanic, any race) + $21.86T (Black) + $19.38T (Asian).
                        </WppTypography>
                    </div>
                </div>
            </div>
        ),
        [SideModalTitle.INCOME_LEVELS]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    This plot illustrates category spend by Ethnic group, sliced by disposable
                    income and geography.
                    <br />
                    <br />
                    Disposable income encompasses budget left over after taxes, housing costs, and
                    related costs such as utilities. Cash contributions, such as alimony, child
                    support, and college student support, are also subtracted from the net income.
                    In instances where financial data predating 2023 is utilized, monetary values
                    have been adjusted to account for inflation, using mid-2022 as the reference
                    point.
                    <br />
                </WppTypography>
            </div>
        ),
        [SideModalTitle.CATEGORIES]: (
            <div className={Main}>
                <div>
                    <WppTypography type="s-strong">Summary</WppTypography>
                    <WppTypography type="s-body">
                        This tab projects future category spend by building on top of the baseline
                        disposable income socioeconomic scenario in the Business Opportunity tab.
                        Category spend is calculated based on modelling current audience spending
                        habits and evolution of disposable income.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Disposable income</WppTypography>
                    <WppTypography type="s-body">
                        Disposable income encompasses budget left over after taxes, housing costs,
                        and related costs such as utilities. Cash contributions, such as alimony,
                        child support, and college student support, are also subtracted from the net
                        income. In instances where financial data predating 2023 is utilized,
                        monetary values have been adjusted to account for inflation, using mid-2022
                        as the reference point.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Categories</WppTypography>
                    <WppTypography type="s-body">
                        Categories and subcategories are based on common brand verticals. For a
                        detailed breakdown of what is contained in each Category-Subcategory
                        combination, please refer to the “Methodology” document provided in the
                        “Overview” landing page.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Interpreting plot tooltips</WppTypography>
                    <WppTypography type="s-body">
                        If you select “Socioeconomic mobility” under “Group by”, the tooltip value
                        will represent individual disposable income per scenario. If you select any
                        other option under “Group by”, then each number to the left of the
                        parentheses represents the individual disposable income value you’re
                        hovering over in the plot. The corresponding number in parentheses (“n%”)
                        represents the projection as a percentage out of the total individual
                        disposable income in the plot.
                    </WppTypography>
                </div>
                <div className={MainFooter}>
                    <div className={MainFooterContent}>
                        <WppTypography type="s-strong">Sample insights</WppTypography>
                        <WppTypography type="s-body">
                            Audience definition: All geo regions, groceries: pet food, all
                            ethnicities, all ages, all sexes, all disposable income levels.
                            <br />
                            Group by: Ethnic group.
                            <br />
                            Show by: Subcategories.
                            <br />
                            Spend view: Per year.
                            <br />
                            <br />
                            Spending on pet food is projected to increase over time across the
                            entire population.
                            <br />
                            <br />
                            All ethnically diverse groups increase spend on pet food at a higher
                            rate than the White Ethnic group. This observation is in line with the
                            increasing ethnically diverse population in the Population and
                            Demographics tabs, and the upward Disposable income trajectories from
                            the Baseline scenario in the Business Opportunity tab.
                            <br />
                            <br />
                            Learn how increases in the population and per person spend contribute to
                            category trajectories in the Drivers of Growth tab. Simulate how your
                            client can capitalize on these business opportunities with custom
                            projections by clicking on the “+ Create scenario” button.
                        </WppTypography>
                    </div>
                </div>
            </div>
        ),
        [SideModalTitle.POPULATION_SPEND]: (
            <div className={Main}>
                <div>
                    <WppTypography type="s-body">
                        This tab provides a high-level overview of the sources driving growth in
                        category spend. Category growth can occur from an increase in consumers
                        within a growing audience group, or from an increase in the purchase power
                        of the audience, leading to higher per person spend within the category. The
                        magnitude of each source of growth can be used to inform brand strategy for
                        “quantity” vs “quality” questions. For example, a brand could analyze
                        drivers of growth to choose between increasing mainstream product range
                        investment vs increasing marketing for high tier/luxury products to appeal
                        to consumers with increased purchase power.
                    </WppTypography>
                    <br />
                    <br />
                    <WppTypography type="s-strong">
                        Definitions and tool tip interpretation
                    </WppTypography>
                    <WppTypography type="s-body">
                        Population refers to the percentage increase in category purchases relative
                        to 2024, while spend reflects the percentage increase in category spend per
                        person relative to the same year. These two elements represent the sources
                        of growth, accounting for the percent of total growth from each source. The
                        contribution of each source of growth can be further analyzed based on user
                        selections in ’show by’ and their ’x-axis’ selection.
                    </WppTypography>
                </div>
                <div className={MainFooter}>
                    <div className={MainFooterContent}>
                        <WppTypography type="s-strong">Sample insights</WppTypography>
                        <WppTypography type="s-body">
                            As a general trend, in the short term, more of the growth stems from
                            changes in consumer spending. In the mid to long term, the population
                            can contribute significantly to driving growth. Looking into Juice
                            subcategory for Hispanic (any race) population, in a 3-year short term
                            timeline (2027), Spend as a driver of growth far exceeds Population as a
                            driver by a 12% margin. This difference is despite Hispanic population
                            being among the fastest growing audiences. However, as the size of this
                            population increases in the long term (2040), the delta between both
                            drivers is reduced to 9% as the audience size contributes significantly
                            more to growth. This indicates the need for a multi-stage strategy for
                            short vs long-term brand investment.
                            <br />
                            <br />
                            To learn what population growth and category spend can mean for your
                            business, click on “Create Scenario” to generate customized projections.
                        </WppTypography>
                    </div>
                </div>
            </div>
        ),
        [SelectionHighlighted.TOTAL_DEFINED_SEGMENT]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    This plot shows projections for your total defined audience.
                </WppTypography>
            </div>
        ),
        [SelectionHighlighted.SEX]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    This plot option shows projections by sex. Sex is mutually exclusive. This is
                    strictly based on the taxonomy provided in the source datasets.
                </WppTypography>
            </div>
        ),
        [SelectionHighlighted.AGE_GROUP]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    This plot shows projections by age group.
                    <br />
                    <br />
                    The simulation incorporates real world ageing behaviour. Every year some people
                    will age into your selected age band(s), and some will leave. As a result,
                    you`ll see additional age groups in the plots that you didn`t select as inputs.
                    Let`s say you select the less 30 (16-29) group. People who were 15 in 2022 will
                    not be counted in the 2022 projections because they haven`t aged into the 16-29
                    group. They will be counted in 2023 because they will be 16 by then. In 14
                    years, they will age into the 30-39 band because they`ll be 30 by then.
                </WppTypography>
            </div>
        ),
        [SelectionHighlighted.ETHNIC_GROUP]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    This plot shows projections by Ethnic group.
                    <br />
                    <br />
                    See the “See more” modal in the Population tab for details about Ethnic group
                    definitions.
                </WppTypography>
            </div>
        ),
        [SelectionHighlighted.CATEGORY]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    Categories are based on common spend verticals. Select a category in your
                    defined scenario from the drop-down menu to zoom into the vertical and learn
                    about granular breakdown into subcategories.
                </WppTypography>
            </div>
        ),
        [SelectionHighlighted.SUBCATEGORY]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    Subcategories provide granular breakdowns of category spend.
                </WppTypography>
            </div>
        ),
        [SideModalTitle.HOW_TO_USE]: (
            <WppTypography type="s-body">
                Create your own Alternative Future with your custom segment opportunity simulation.
                Define an audience segment based on target demographics, select categories of
                interest, and enter a category engagement uplift %.
                <br />
                <br />
                This screen uses data from the Demographic, Business Opportunity, and Current Spend
                tabs as a starting point. You can build onto this starting point with different
                “what if“ category engagement uplift percentages.
                <br />
                <br />
                Engagement % refers to the percentage points of your segment(s)’ spend in selected
                category(ies). The increase in engagement % represents additional uplift to the
                organic growth of your audience and category spend. Interpretation depends on your
                use case. From a macroeconomic perspective, engagement % can be interpreted as the
                percentage point increase in your segment(s)’ category engagement, such that there
                is additional category growth. Alternatively, from a brand perspective, it can
                represent the percentage point increase of your segment(s)’ engagement with the
                brand. This refers to switching from competitors into your client’s product
                portfolio due to marketing. Engagement % can also represent a combination of the
                two.
                <br />
                <br />
                You can filter projections by geographic area using the geography selector. You can
                filter by Region(s) or DMA, but not both simultaneously. Regions are multi-select.
                DMAs are single-select.
                <br />
                <br />
                Run your scenarios and see how they play out in the resulting category spend and
                population growth plots. Drill down to uncover insights by filtering data by Age,
                Sex, Ethnic group, Category, and Subcategory.
            </WppTypography>
        ),
        [SideModalTitle.COMPLIANCE]: (
            <WppTypography type="xs-body">
                © Bureau of Labour Statistics. Consumer Expenditure Survey: Public Use Microdata
                Files (PUMD) (2013 – 2021). Used for both disposable income and category spend.
                <ul>
                    <li>
                        - Data available at{' '}
                        <a
                            href="https://www.bls.gov/cex/pumd_data.htm"
                            target="_blank"
                            rel="noreferrer"
                        >
                            here
                        </a>
                        .
                    </li>
                    <li>
                        - License:{' '}
                        <a
                            href="https://resources.data.gov/open-licenses/"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Federal Enterprise Data Resources
                        </a>{' '}
                        and{' '}
                        <a
                            href="https://www.bls.gov/bls/linksite.htm"
                            target="_blank"
                            rel="noreferrer"
                        >
                            Bureau of Labor Statistics
                        </a>
                    </li>
                </ul>
                <br />
                <br />© Census Bureau. 2017 National Population Projection Datasets (2016 - 2060).
                <ul>
                    <li>
                        - Used for: population projections at National level, validating projections
                        at regional level against and obtaining Age-Specific Fertility Rates (ASFR)
                        for use in generating population projections at smaller scales.
                    </li>
                    <li>
                        - Data: download tables 1 and 6{' '}
                        <a
                            href="https://www.census.gov/data/datasets/2017/demo/popproj/2017-popproj.html"
                            target="_blank"
                            rel="noreferrer"
                        >
                            here
                        </a>
                        .
                    </li>
                    <li>
                        - License: Federal Open Data Policy. See{' '}
                        <a
                            href="https://www.census.gov/about/policies/open-gov/open-data.html"
                            target="_blank"
                            rel="noreferrer"
                        >
                            here
                        </a>
                        .{' '}
                    </li>
                </ul>
                <br />
                <br />© Census Bureau. Population Estimates (2019 – 2021), Decennial Census (2010)
                and Provisional Decennial Census (2020).
                <ul>
                    <li>
                        - Used for: generate population projections at regional level and validation
                        exercises.
                    </li>
                    <li>
                        - Data accessed via API{' '}
                        <a
                            href="https://www.census.gov/data/developers/data-sets/popest-popproj/popest.html"
                            target="_blank"
                            rel="noreferrer"
                        >
                            here
                        </a>
                        .
                    </li>
                    <li>- License: Federal Open Data Policy.</li>
                </ul>
                <br />
                <br />© Centres for Disease Control and Prevention (National Vital Statistics
                System). County-level Population Estimates (2016 – 2021).
                <ul>
                    <li>
                        - Used for: creating Designated Market Area (DMA) level population
                        projections.
                    </li>
                    <li>
                        - Data: 2020 vintage, July 1 download{' '}
                        <a
                            href="https://www.cdc.gov/nchs/nvss/bridged_race/data_documentation.htm#vintage2020"
                            target="_blank"
                            rel="noreferrer"
                        >
                            here
                        </a>
                        .
                    </li>
                    <li>
                        - License: Assuming it also comes under Federal Open Data Policy as data is
                        “prepared under a collaborative agreement with the US Census Bureau”. See{' '}
                        <a
                            href="https://www.cdc.gov/nchs/nvss/bridged_race.htm#:~:text=National%20bridged-race%20postcensal%20estimates,Hispanic%20or%20Latino%2C%20Hispanic%20or"
                            target="_blank"
                            rel="noreferrer"
                        >
                            here
                        </a>
                        .{' '}
                    </li>
                    <li>
                        - Additional Legal Requirements:
                        <ul>
                            <li>
                                1. The Simulator must refer to the agency that developed the
                                materials and must clearly state that the materials were developed
                                by CDC, ATSDR and/or HHS (e.g., “Source: CDC”; “Materials developed
                                by CDC”);{' '}
                            </li>
                            <li>
                                2. The Simulator must utilize a disclaimer which clearly indicates
                                its use of the materials, including any links to the materials on
                                the CDC, ATSDR, or HHS website, does not imply an endorsement by the
                                CDC, ATSDR, HHS or the United Statements Government of the Simulator
                                and Choreograph. The disclaimer must be prominent and unambiguously
                                displayed (e.g. “Reference to specific commercial products,
                                manufacturers, companies or trademarks does not constitute its
                                endorsement or recommendation by the US Government, HHS or CDC.).;{' '}
                            </li>
                            <li>
                                3. The Simulator must not change the substantive content of the
                                materials.
                            </li>
                            <li>
                                4. The Simulator must state that the material is otherwise available
                                on the agency website at no charge.
                            </li>
                        </ul>
                    </li>
                    <br />© Esri ArcGIS. USA County Generalised Boundaries (2020) (created using US
                    Census Bureau data).
                    <ul>
                        <li>
                            - Used for: identifying relevant counties that are within chosen DMAs.
                        </li>
                        <li>
                            - Data: download{' '}
                            <a
                                href="https://www.arcgis.com/home/item.html?id=3c164274a80748dda926a046525da610"
                                target="_blank"
                                rel="noreferrer"
                            >
                                here
                            </a>
                            .
                        </li>
                        <li>
                            License: Federal Open Data Policy. See{' '}
                            <a
                                href="https://www.census.gov/about/policies/open-gov/open-data.html"
                                target="_blank"
                                rel="noreferrer"
                            >
                                here
                            </a>
                        </li>
                    </ul>
                </ul>
                <br />
            </WppTypography>
        ),
        [SideModalTitle.DEFAULT]: (
            <WppTypography type="s-body">
                Sub-categories are based on common verticals under a selected category. For extended
                granularity for your category of interest, please get in touch{' '}
                <a href="mailto:growth@choreograph.com?subject = Feedback&body = Message">
                    (growth@choreograph.com)
                </a>
                <br />
            </WppTypography>
        ),
    },
    [Localization.UK]: {
        [SideModalTitle.POPULATION_GROUPS]: (
            <div className={Main}>
                <div>
                    <WppTypography type="s-strong">Summary</WppTypography>
                    <WppTypography type="s-body">
                        This tab showcases population trends for UK adults (16+). The default
                        setting is the entire adult (16+) UK population. In the remainder of the
                        product, all aggregations, projections, and alternative future scenarios are
                        based on this adult (16+) population.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Context for our definitions</WppTypography>
                    <WppTypography type="s-body">
                        Based on our discussions with GroupM multicultural experts, please note the
                        following:
                    </WppTypography>
                    <ul className={MainList}>
                        <li>
                            The Segment Opportunity Simulator refers to “ethnically diverse groups“
                            as “ethnic groups“, encompassing (Black, East Southeast Asian, Middle
                            Eastern, Mixed and Southern Asian).
                        </li>
                    </ul>
                </div>
                <div>
                    <WppTypography type="s-strong">Adult population</WppTypography>
                    <br />
                    <WppTypography type="s-body">
                        <ul className={MainList}>
                            <li>The adult population is defined as 16+.</li>
                        </ul>
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Diverse Populations</WppTypography>
                    <br />
                    <WppTypography type="s-body">
                        <ul className={MainList}>
                            <li>
                                <span className={MainStrong}>Black: </span>Individuals identified as
                                “Black“ in the survey.
                            </li>
                            <li>
                                <span className={MainStrong}>Asian: </span> Includes ethnicities
                                from the Middle East, South and Southeast.
                            </li>
                            <li>
                                <span className={MainStrong}>Mixed race: </span>
                                Individuals of multiple racial backgrounds or those from smaller,
                                less represented races.
                            </li>
                            <li>
                                <span className={MainStrong}>White: </span>Individuals who
                                identified as “White“ as their only race. Those who identified with
                                other races and/or ethnicities are not included in this category.
                            </li>
                        </ul>
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Interpreting plot tooltips</WppTypography>
                    <WppTypography type="s-body">
                        <ul className={MainList}>
                            <li>
                                When you Group by ”Total population”, the ”Adult population (16+)”
                                value represents the total adults and” Total population” represents
                                the total population, inclusive of children.
                            </li>
                            <li>
                                Other “Group By” options provide different tooltips reflecting the
                                grouping and aggregation for that dropdown. For example, “Combined
                                Multicultural” shows “White” and “Ethnically Diverse Groups” to
                                highlight the growth in all Ethnic Groups combined vs, the steady
                                decline of the White population.
                            </li>
                        </ul>
                    </WppTypography>
                </div>
                <div className={MainFooter}>
                    <div className={MainFooterContent}>
                        <WppTypography type="s-strong">Sample insights</WppTypography>
                        <WppTypography type="s-body">
                            <ul className={MainList}>
                                <li>
                                    <span className={MainStrong}>Audience definition: </span>All
                                    ethnicities, All ages, All sexes.
                                </li>
                                <li>
                                    <span className={MainStrong}>Group by: </span>Combined
                                    Multicultural.
                                </li>
                                <li>
                                    <span className={MainStrong}>Show by: </span>No aggregation.
                                </li>
                            </ul>
                            <br />
                            The plots indicate that the adult population (16+) of ethnically diverse
                            groups is growing, and the White segment is projected to plateau by
                            2060, indicating slow growth for this population. In contrast, the
                            ethnically diverse groups population are expected to grow 50% in the
                            same time.
                        </WppTypography>
                    </div>
                </div>
            </div>
        ),
        [SideModalTitle.DEMOGRAPHIC_GROUPS]: (
            <div className={Main}>
                <div>
                    <WppTypography type="s-strong">Summary</WppTypography>
                    <WppTypography type="s-body">
                        This tab provides demographic breakdowns of the population projections.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Note on definitions</WppTypography>
                    <WppTypography type="s-body">
                        See the “Learn more” modal in the Population tab for details about Ethnic
                        group definitions. Sex is mutually exclusive. This is strictly based on the
                        taxonomy provided in the source datasets.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Interpreting plot tooltips</WppTypography>
                    <br />
                    <WppTypography type="s-body">
                        The ”Adult Population” value to the left of the parentheses represents the
                        population projection you`re hovering over in the plot. The corresponding
                        number in parentheses (”n%”) represents the projection as a percentage out
                        of the total values in the plot. The ”Total up to this point” value
                        represents the total population of the group and all the groups below it in
                        the legend (stacked from bottom to top).
                    </WppTypography>
                </div>
                <div className={MainFooter}>
                    <div className={MainFooterContent}>
                        <WppTypography type="s-strong">Sample insights</WppTypography>
                        <WppTypography type="s-body">
                            <ul className={MainList}>
                                <li>
                                    <span className={MainStrong}>Audience definition: </span>All
                                    ethnicities, All ages, All sexes.
                                </li>
                                <li>
                                    <span className={MainStrong}>Group by: </span>Ethnic group.
                                </li>
                                <li>
                                    <span className={MainStrong}>Show by: </span>Age.
                                </li>
                            </ul>
                            <br />
                            30% of younger consumers (16-39) will be ethnically/racially diverse
                            (non-White) by 2060. The takeaway is that an increasing number of new
                            consumers from diverse groups will come of age and enter the market,
                            expanding your business opportunities.
                            <br />
                            <br />
                            Learn about possible individual disposable income trajectories for
                            different ethnic groups by specifying disposable income in the dropdowns
                            on the left-hand side. For more details, see the Business Opportunity
                            tab.
                        </WppTypography>
                    </div>
                </div>
            </div>
        ),
        [SideModalTitle.MOBILITY_SCENARIOS]: (
            <div className={Main}>
                <div>
                    <WppTypography type="s-strong">Summary</WppTypography>
                    <WppTypography type="s-body">
                        This tab showcases how UK disposable income may evolve over time based on
                        different socioeconomic mobility levels. The projections build on the
                        insights in the previous tabs by introducing four alternative future
                        scenarios based on socioeconomic mobility evolutions. The recommended
                        socioeconomic mobility is the ”Baseline” scenario and the remainder of the
                        product bases the projections on this scenario.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Mobility scenarios</WppTypography>
                    <WppTypography type="s-body">
                        There are four potential socioeconomic mobility scenarios. In every
                        scenario, the population grows over time. As a result, total individual
                        disposable income for the population also increases. The recommended
                        socioeconomic mobility is the ”Baseline” scenario and the remainder of the
                        product bases the projections on the ”Baseline”. Different scenarios reflect
                        the overarching trend of growing diverse purchase power, albeit the exact
                        quantitative values changing.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Baseline</WppTypography>
                    <WppTypography type="s-body">
                        Assumes that socio-economic mobility trends from the past twenty years will
                        continue for the next twenty and then gradually slow down. Applies to all
                        ethnicities.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">No socioeconomic mobility</WppTypography>
                    <WppTypography type="s-body">
                        Assumes that future income will be constant based on 2021 levels. Applies to
                        all ethnicities.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Rapid socioeconomic mobility</WppTypography>
                    <WppTypography type="s-body">
                        A variant of the Baseline scenario, with more rapid socio-economic mobility.
                        Applies to all ethnicities.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">
                        Persistent socio-economic mobility
                    </WppTypography>
                    <WppTypography type="s-body">
                        The Southern Asian Indian demographic has the highest income in the UK in
                        2025, across all age and sex groups. In this scenario, all ethnicities
                        follow similar growth curves with a view to achieve parity with the Indian
                        group by 2060. Applies to all Minority Ethnic groups.
                        <br />
                        <br />
                        Disposable income is based on the UK ONS’s definition. It encompasses the
                        budget left over after taxes, housing costs, and related costs such as
                        utilities.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Interpreting plot tooltips</WppTypography>
                    <WppTypography type="s-body">
                        If you select “Socioeconomic mobility” under “Group by”, the tooltip value
                        will represent individual disposable income per scenario.
                        <br />
                        If you select any other option under “Group by”, then each number to the
                        left of the parentheses represents the individual disposable income value
                        you’re hovering over in the plot. The corresponding number in parentheses
                        (“n%”) represents the projection as a percentage out of the total individual
                        disposable income in the plot.
                    </WppTypography>
                </div>
                <div className={MainFooter}>
                    <div className={MainFooterContent}>
                        <WppTypography type="s-strong">Sample insights</WppTypography>
                        <WppTypography type="s-body">
                            Audience definition: All ethnicities, all ages, all sexes, all incomes
                            <br />
                            Group by: Ethnic group.
                            <br />
                            Show by: Socioeconomic scenario
                            <br />
                            <br />
                            Everyone wins when underserved populations are financially empowered.
                            ethnically/racially diverse (non-White) groups gain more than £14
                            trillion in cumulative individual disposable income [1] in a world where
                            they have rapid socioeconomic mobility vs no mobility.
                            <br />
                            <br />
                            Disposable income is projected to stabilize for the White group and
                            whereas the ethnically diverse groups show more growth in most
                            scenarios. These insights are particularly visible in the Per Year view,
                            which allows you to do a year-by-year comparison.
                        </WppTypography>
                    </div>
                </div>
            </div>
        ),
        [SideModalTitle.INCOME_LEVELS]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    This plot illustrates category spend by Ethnic group, sliced by disposable
                    income and geography.
                    <br />
                    <br />
                    Disposable income encompasses budget left over after taxes, housing costs, and
                    related costs such as utilities. Cash contributions, such as alimony, child
                    support, and college student support, are also subtracted from the net income.
                    In instances where financial data predating 2023 is utilized, monetary values
                    have been adjusted to account for inflation, using mid-2022 as the reference
                    point.
                    <br />
                </WppTypography>
            </div>
        ),
        [SideModalTitle.CATEGORIES]: (
            <div className={Main}>
                <div>
                    <WppTypography type="s-strong">Summary</WppTypography>
                    <WppTypography type="s-body">
                        This tab projects future category spend by building on top of the baseline
                        disposable income socioeconomic scenario in the Disposable Income tab.
                        Category spend is calculated based on modelling current audience spending
                        habits and evolution of disposable income.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Disposable income</WppTypography>
                    <WppTypography type="s-body">
                        Disposable income encompasses budget left over after taxes, housing costs,
                        and related costs such as utilities. Cash contributions, such as alimony,
                        child support, and college student support, are also subtracted from the net
                        income. In instances where financial data predating 2023 is utilized,
                        monetary values have been adjusted to account for inflation, using mid-2022
                        as the reference point.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Categories</WppTypography>
                    <WppTypography type="s-body">
                        Categories and subcategories are based on common brand verticals. For a
                        detailed breakdown of what is contained in each Category-Subcategory
                        combination, please refer to the “Methodology” document provided in the
                        “Overview” landing page.
                    </WppTypography>
                </div>
                <div>
                    <WppTypography type="s-strong">Interpreting plot tooltips</WppTypography>
                    <WppTypography type="s-body">
                        If you select “Socioeconomic mobility” under “Group by”, the tooltip value
                        will represent individual disposable income per scenario. If you select any
                        other option under “Group by”, then each number to the left of the
                        parentheses represents the individual disposable income value you’re
                        hovering over in the plot. The corresponding number in parentheses (“n%”)
                        represents the projection as a percentage out of the total individual
                        disposable income in the plot.
                    </WppTypography>
                </div>
                <div className={MainFooter}>
                    <div className={MainFooterContent}>
                        <WppTypography type="s-strong">Sample insights</WppTypography>
                        <WppTypography type="s-body">
                            Audience definition: Groceries: pet care, all ethnicities, all ages, all
                            sexes, all disposable income levels.
                            <br />
                            Group by: Ethnic group.
                            <br />
                            Show by: Subcategories.
                            <br />
                            Spend view: Per year.
                            <br />
                            <br />
                            Spending on pet food is projected to increase over time across the
                            entire population.
                            <br />
                            <br />
                            All ethnically diverse groups increase spend on pet are at a higher rate
                            than the White Ethnic group. This observation is in line with the
                            increasing ethnically diverse population in the Population and
                            Demographics tabs, and the upward Disposable income trajectories from
                            the Baseline scenario in the Disposable Income tab.
                            <br />
                            <br />
                            Learn how increases in the population and per person spend contribute to
                            category trajectories in the Drivers of Growth tab. Simulate how your
                            client can capitalize on these business opportunities with custom
                            projections by clicking on the “+ Create scenario” button.
                        </WppTypography>
                    </div>
                </div>
            </div>
        ),
        [SideModalTitle.POPULATION_SPEND]: (
            <div className={Main}>
                <div>
                    <WppTypography type="s-body">
                        This tab provides a high-level overview of the sources driving growth in
                        category spend. Category growth can occur from an increase in consumers
                        within a growing audience group, or from an increase in the purchase power
                        of the audience, leading to higher per person spend within the category. The
                        magnitude of each source of growth can be used to inform brand strategy for
                        “quantity” vs “quality” questions. For example, a brand could analyse
                        drivers of growth to choose between increasing mainstream product range
                        investment vs increasing marketing for high tier/luxury products to appeal
                        to consumers with increased purchase power.
                    </WppTypography>
                    <br />
                    <br />
                    <WppTypography type="s-strong">
                        Definitions and tool tip interpretation
                    </WppTypography>
                    <WppTypography type="s-body">
                        Population refers to the percentage increase in category purchases relative
                        to 2024, while spend reflects the percentage increase in category spend per
                        person relative to the same year. These two elements represent the sources
                        of growth, accounting for the percent of total growth from each source. The
                        contribution of each source of growth can be further analysed based on user
                        selections in ’show by’ and their ’x- axis’ selection.
                    </WppTypography>
                </div>
                <div className={MainFooter}>
                    <div className={MainFooterContent}>
                        <WppTypography type="s-strong">Sample insights</WppTypography>
                        <WppTypography type="s-body">
                            As a general trend, in the short term, more of the growth stems from
                            changes in consumer spending. In the mid to long term, the population
                            can contribute significantly to driving growth.
                            <br />
                            <br />
                            Looking into Groceries category for South Asian (Indian) population by
                            age group, in a 5-year short term timeline (2030), Spend as a driver of
                            growth exceeds Population as a driver by a 1% margin. At this stage in
                            the timeline, the 30-39 age group is declining in population by
                            approximately 2%, yet their spending is up 3%, which highlights the
                            sheer spending power of this demographic despite their population size.
                            However, by 2040 the population driver far exceeds spending by a delta
                            of 29% as the audience size contributes significantly more to growth.
                            This indicates the need for a multi-stage strategy for short vs
                            long-term brand investment.
                        </WppTypography>
                    </div>
                </div>
            </div>
        ),
        [SelectionHighlighted.TOTAL_DEFINED_SEGMENT]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    This plot shows projections for your total defined audience.
                </WppTypography>
            </div>
        ),
        [SelectionHighlighted.SEX]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    This plot option shows projections by sex. Sex is mutually exclusive. This is
                    strictly based on the taxonomy provided in the source datasets.
                </WppTypography>
            </div>
        ),
        [SelectionHighlighted.AGE_GROUP]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    This plot shows projections by age group.
                    <br />
                    <br />
                    The simulation incorporates real world ageing behaviour. Every year some people
                    will age into your selected age band(s), and some will leave. As a result,
                    you`ll see additional age groups in the plots that you didn`t select as inputs.
                    Let`s say you select the less 30 (16-29) group. People who were 15 in 2022 will
                    not be counted in the 2022 projections because they haven`t aged into the 16-29
                    group. They will be counted in 2023 because they will be 16 by then. In 14
                    years, they will age into the 30-39 band because they`ll be 30 by then.
                </WppTypography>
            </div>
        ),
        [SelectionHighlighted.ETHNIC_GROUP]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    This plot shows projections by Ethnic group.
                    <br />
                    <br />
                    See the “See more” modal in the Population tab for details about Ethnic group
                    definitions.
                </WppTypography>
            </div>
        ),
        [SelectionHighlighted.CATEGORY]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    Categories are based on common spend verticals. Select a category in your
                    defined scenario from the drop-down menu to zoom into the vertical and learn
                    about granular breakdown into subcategories.
                </WppTypography>
            </div>
        ),
        [SelectionHighlighted.SUBCATEGORY]: (
            <div className={Main}>
                <WppTypography type="s-body">
                    Subcategories provide granular breakdowns of category spend.
                </WppTypography>
            </div>
        ),
        [SideModalTitle.HOW_TO_USE]: (
            <WppTypography type="s-body">
                Create your own Alternative Future with your custom segment opportunity simulation.
                Define an audience segment based on target demographics, select categories of
                interest, and enter a category engagement uplift %.
                <br />
                <br />
                This screen uses data from the Demographic, Business Opportunity, and Current Spend
                tabs as a starting point. You can build onto this starting point with different
                “what if“ category engagement uplift percentages.
                <br />
                <br />
                Engagement % refers to the percentage points of your segment(s)’ spend in selected
                category(ies). The increase in engagement % represents additional uplift to the
                organic growth of your audience and category spend. Interpretation depends on your
                use case. From a macroeconomic perspective, engagement % can be interpreted as the
                percentage point increase in your segment(s)’ category engagement, such that there
                is additional category growth. Alternatively, from a brand perspective, it can
                represent the percentage point increase of your segment(s)’ engagement with the
                brand. This refers to switching from competitors into your client’s product
                portfolio due to marketing. Engagement % can also represent a combination of the
                two.
                <br />
                <br />
                You can filter projections by geographic area using the geography selector. You can
                filter by Region(s) or DMA, but not both simultaneously. Regions are multi-select.
                DMAs are single-select.
                <br />
                <br />
                Run your scenarios and see how they play out in the resulting category spend and
                population growth plots. Drill down to uncover insights by filtering data by Age,
                Sex, Ethnic group, Category, and Subcategory.
            </WppTypography>
        ),
        [SideModalTitle.COMPLIANCE]: (
            <WppTypography type="xs-body">
                © Wohland, P., Rees, P., Norman, P., Lomax, N. & Clark, S., 2018. The NEWETHPOP
                (Leeds2Projections): Ethnic population projections for UK local areas 2011-2061 are
                licensed under CC BY 4.0.
                <br />
                <br />© Office for National Statistics, 2011. The 2011 England & Wales census data
                is licensed under the Open Government Licence 3.0.
                <br />
                <br />© National Records of Scotland, 2011. The 2011 Scotland census data is
                licensed under the Open Government Licence 3.0.
                <br />
                <br />© UK Government, 2020. The Households below average income dataset is licensed
                under the Open Government Licence 3.0.
                <br />
                <br />© Office for National Statistics, 2020. The Family spend in the UK workbook 1
                is licensed under the Open Government Licence 3.0.
            </WppTypography>
        ),
        [SideModalTitle.DEFAULT]: (
            <WppTypography type="s-body">
                Sub-categories are based on common verticals under a selected category. For extended
                granularity for your category of interest, please get in touch{' '}
                <a href="mailto:growth@choreograph.com?subject = Feedback&body = Message">
                    (growth@choreograph.com)
                </a>
                <br />
            </WppTypography>
        ),
    },
};
